/**
 * Login / register modal shown when a guest tries to run a CV action.
 * Stays on the current page so selected files and editor drafts are kept.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import GoogleSignInButton from './GoogleSignInButton';
import TurnstileWidget from './TurnstileWidget';
import { trackGaEvent, trackSignUp } from '../utils/analytics';
import { POST_SIGNUP } from '../utils/paths';
import '../pages/Login.css';
import './AuthGateModal.css';

const GOOGLE_ERROR_KEYS = {
  google_disabled: 'login.errGoogleUnavailable',
  google_unavailable: 'login.errGoogleUnavailable',
  missing_credential: 'login.errGoogle',
  invalid_token: 'login.errGoogle',
  email_unverified: 'login.errGoogleUnverified',
  no_email: 'login.errGoogleNoEmail',
  account_disabled: 'login.errAccountDisabled',
  create_failed: 'login.errGoogle',
  google_auth_failed: 'login.errGoogle',
  rate_limited: 'login.errRateLimited',
};

const AuthGateModal = ({ open, onClose }) => {
  const { login, loginWithGoogle, register } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const closeRef = useRef(null);
  const [isLogin, setIsLogin] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    re_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingActivationEmail, setPendingActivationEmail] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleUnavailable, setGoogleUnavailable] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setIsLogin(false);
    setError('');
    setPendingActivationEmail('');
    setTurnstileToken('');
    setFormData({ email: '', password: '', re_password: '' });
    const timer = window.setTimeout(() => closeRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    const baked = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
    const publicApi = axios.create({ baseURL: apiBase });
    publicApi
      .get('/api/auth/google/config/')
      .then(({ data }) => {
        const clientId = (data?.client_id || data?.clientId || '').trim();
        if ((data?.enabled && clientId) || baked) {
          setGoogleClientId(baked || clientId);
        }
        const siteKey = (data?.turnstile_site_key || '').trim();
        if (data?.turnstile_enabled && siteKey) {
          setTurnstileSiteKey(siteKey);
        }
      })
      .catch(() => {
        if (baked) {
          setGoogleClientId(baked);
        }
      });
    return undefined;
  }, []);

  const friendlyError = (message) => {
    if (!message || typeof message !== 'string') return t('login.errGeneric');
    const m = message.toLowerCase();
    if (m.includes('email') && (m.includes('already exists') || m.includes('already registered'))) {
      return t('login.errEmailExists');
    }
    if (
      m.includes('no active account') ||
      m.includes('inactive') ||
      m.includes('not active') ||
      m.includes('user is inactive')
    ) {
      return t('login.errInactive');
    }
    if (m.includes('invalid') && (m.includes('credential') || m.includes('password'))) {
      return googleClientId ? t('login.errCredentialsGoogle') : t('login.errCredentials');
    }
    if (m.includes('password') && (m.includes('too short') || m.includes('common') || m.includes('similar'))) {
      return t('login.errWeakPassword');
    }
    if (m.includes('password') && m.includes('match')) {
      return t('login.errPasswordMatch');
    }
    if (m.includes('google')) {
      return t('login.errGoogle');
    }
    return message;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (!result.success) {
          setError(friendlyError(result.error));
        }
      } else {
        if (formData.password !== formData.re_password) {
          setError(t('login.errPasswordMatch'));
          setLoading(false);
          return;
        }
        if (turnstileSiteKey && !turnstileToken) {
          setError(t('login.errCaptcha'));
          setLoading(false);
          return;
        }
        const payload = {
          ...formData,
          username: formData.email || 'user',
          first_name: '',
          last_name: '',
          turnstile_token: turnstileToken,
        };
        const result = await register(payload);
        if (result.success) {
          const email = formData.email;
          trackSignUp('email');
          setPendingActivationEmail(email);
          setTurnstileToken('');
          setFormData({
            email,
            password: '',
            re_password: '',
          });
        } else {
          setTurnstileToken('');
          setError(friendlyError(result.error));
        }
      }
    } catch (err) {
      setError(friendlyError(t('login.errUnexpected')));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(
    async (credential) => {
      setLoading(true);
      setError('');
      try {
        const result = await loginWithGoogle(credential);
        if (result.success) {
          if (result.created) {
            trackSignUp('google');
            const path = location.pathname || '';
            const onWork =
              path.startsWith('/dashboard/upload') ||
              path.startsWith('/dashboard/quick-edit') ||
              path.startsWith('/dashboard/keep-look') ||
              path.startsWith('/dashboard/cv') ||
              path.startsWith('/dashboard/edit-cv');
            if (!onWork) {
              navigate(POST_SIGNUP, { replace: true });
            }
          } else {
            trackGaEvent('login', { method: 'google' });
          }
        } else {
          const key = GOOGLE_ERROR_KEYS[result.errorCode];
          setError(key ? t(key) : t('login.errGoogle'));
        }
      } catch (err) {
        setError(friendlyError(t('login.errGoogle')));
      } finally {
        setLoading(false);
      }
    },
    [loginWithGoogle, t, navigate, location.pathname],
  );

  if (!open) {
    return null;
  }

  const showCheckEmail = Boolean(pendingActivationEmail);
  const title = showCheckEmail
    ? t('login.successRegisterTitle')
    : isLogin
      ? t('authGate.titleLogin')
      : t('authGate.titleRegister');
  const lead = showCheckEmail
    ? t('login.successRegister', { email: pendingActivationEmail })
    : t('authGate.lead');

  return (
    <div className="auth-gate" role="presentation" onClick={onClose}>
      <div
        className="auth-gate__dialog auth-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-gate-title"
        aria-describedby="auth-gate-lead"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          className="auth-gate__close"
          onClick={onClose}
          aria-label={t('authGate.closeAria')}
        >
          ×
        </button>
        <h2 id="auth-gate-title" className="auth-card__title">
          {title}
        </h2>
        <p id="auth-gate-lead" className="auth-card__lead">
          {lead}
        </p>

        {showCheckEmail ? (
          <div className="auth-check-email">
            <ol className="auth-check-email__steps">
              <li>{t('login.successRegisterStep1')}</li>
              <li>{t('login.successRegisterStep2')}</li>
              <li>{t('login.successRegisterStep3')}</li>
              <li>{t('login.successRegisterStep4')}</li>
            </ol>
            <button
              type="button"
              className="btn btn-primary auth-card__submit"
              onClick={() => {
                setPendingActivationEmail('');
                setIsLogin(true);
              }}
            >
              {t('login.goToLoginAfterRegister')}
            </button>
          </div>
        ) : (
          <>
            {error ? (
              <div className="auth-alert auth-alert--error" role="alert">
                <span>{error}</span>
              </div>
            ) : null}
            {googleClientId && !googleUnavailable ? (
              <>
                <GoogleSignInButton
                  clientId={googleClientId}
                  onCredential={handleGoogleCredential}
                  onUnavailable={() => setGoogleUnavailable(true)}
                  disabled={loading}
                  locale={lang}
                  text={isLogin ? 'signin_with' : 'signup_with'}
                />
                <div className="auth-divider">{t('login.orEmail')}</div>
              </>
            ) : null}
            {googleUnavailable ? (
              <p className="auth-card__lead">{t('login.errGoogleUnavailable')}</p>
            ) : null}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="auth-gate-email">{t('login.email')}</label>
                <input
                  type="email"
                  id="auth-gate-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="auth-gate-password">{t('login.password')}</label>
                <input
                  type="password"
                  id="auth-gate-password"
                  name="password"
                  data-clarity-mask="true"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  required
                />
              </div>
              {!isLogin ? (
                <div className="form-group">
                  <label htmlFor="auth-gate-re-password">{t('login.confirmPassword')}</label>
                  <input
                    type="password"
                    id="auth-gate-re-password"
                    name="re_password"
                    data-clarity-mask="true"
                    value={formData.re_password || ''}
                    onChange={handleChange}
                    autoComplete="new-password"
                    required
                  />
                </div>
              ) : null}
              {!isLogin && turnstileSiteKey ? (
                <TurnstileWidget
                  siteKey={turnstileSiteKey}
                  language={lang === 'en' ? 'en' : 'es'}
                  onToken={(token) => {
                    setTurnstileToken(token);
                    setError('');
                  }}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => {
                    setTurnstileToken('');
                    setError(t('login.errCaptcha'));
                  }}
                />
              ) : null}
              <button
                type="submit"
                className="btn btn-primary auth-card__submit"
                disabled={loading || (!isLogin && Boolean(turnstileSiteKey) && !turnstileToken)}
              >
                {loading
                  ? t('login.submitting')
                  : isLogin
                    ? t('login.submitLogin')
                    : t('login.submitRegister')}
              </button>
            </form>
            <div className="auth-card__switch">
              {isLogin ? (
                <Link
                  to="/login?mode=forgot"
                  className="auth-card__switch-btn auth-card__forgot"
                  onClick={onClose}
                >
                  {t('login.forgotLink')}
                </Link>
              ) : null}
              <button
                type="button"
                className="auth-card__switch-btn"
                onClick={() => {
                  setIsLogin((prev) => !prev);
                  setError('');
                  setTurnstileToken('');
                }}
              >
                {isLogin ? t('login.switchToRegister') : t('login.switchToLogin')}
              </button>
            </div>
          </>
        )}

        <button type="button" className="auth-gate__explore" onClick={onClose}>
          {t('authGate.close')}
        </button>
        <p className="auth-card__legal">
          {t('login.privacyNote')} <Link to="/privacy">{t('login.privacy')}</Link>{' '}
          {t('login.termsNote')} <Link to="/terms">{t('login.terms')}</Link>.
        </p>
      </div>
    </div>
  );
};

AuthGateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AuthGateModal;
